import React, { FC, useState, useCallback } from 'react';
import { RichText } from '@sitecore-jss/sitecore-jss-react';
import { BreakpointsInPx } from '../../../../constants/breakpoints.constants';
import useMediaQuery from '../../../../lib/custom-hooks/useMediaQuery';
import { TRendition } from '../../../../types/types';
import { googleClickCtaEvent, googleClickOpenVideoEvent } from '../../../../utils/analytics';
import { getThumbnailProps } from '../../../../utils/youtubeUtils';
import { targetLinkdsk } from '../../../../utils/targetDskLink';
import MediaElement from '../../../@shared/configurationCardMediaElement';
import Icon from '../../../@shared/icon';
import ModalDialog from '../../../@shared/modalDialog';
import OverlayCarouselPopup from '../../../@shared/overlayCarouselPopup';
import { TGalleryItem } from '../../../@shared/overlayCarouselPopup/types/overlayCarouselPopup.types';
import RoutableNotEditableLink from '../../../@shared/routableNotEditableLink';
import { ConfigurationCardModel } from '../../models/productSeriesConfigurationCard.model';
import { TConfigurationsOverviewCard } from '../../types/productSeriesDetailConfigurationsOverview.types';
import { appendRootToRichTextInternalLinks } from '../../../../utils/appendRootToRichTextInternalLinks.utils';
import { appendRootToInternalLink } from '../../../../utils/appendRootToInternalLink.utils';

const ConfigurationCard: FC<TConfigurationsOverviewCard> = (props) => {
  const model = new ConfigurationCardModel(props);

  const [showOverlay, setShowOverlay] = useState(false);

  const isWide = useMediaQuery(`(min-width: ${BreakpointsInPx.md})`);
  const closeOverlayModal = (): void => setShowOverlay(false);

  const showOverlayVideo = (): void => {
    if (!model.isVideo) return;
    setShowOverlay(true);
    googleClickOpenVideoEvent(model.firstAsset.description, model.componentNumber);
  };

  const getOverlayProps: () => TGalleryItem[] = useCallback(() => {
    let externalThumbnail: TRendition | null = null;

    if (model.isVideo) {
      const { showThumbnail, thumbnailImgSrc } = getThumbnailProps(model.firstAsset?.full);

      if (showThumbnail) {
        externalThumbnail = {
          url: thumbnailImgSrc || '',
          height: 720,
          width: 1280,
        };
      }
    }

    return [
      {
        desktopRendition: externalThumbnail?.url ? externalThumbnail : model.firstAsset?.rendition_desktop_size500,
        mobileRendition: externalThumbnail?.url ? externalThumbnail : model.firstAsset?.rendition_mobile_size500,
        desktopAlt: model.firstAsset.alt,
        mobileAlt: model.firstAsset?.alt,
        isVideo: model.isVideo,
        videoUrl: model.getVideoUrl,
      },
    ];
  }, [model.assets]); // eslint-disable-line

  return (
    <article className="configuration-card__article">
      {model.assets?.length > 0 && (
        <div>
          <div
            className={`configuration-card__image-wrapper ${
              model.getVideoUrl ? 'configuration-card__article-picture--fixed-aspect-ratio' : ''
            }`}
          >
            <MediaElement model={model} pictureClassName="configuration-card__article-picture--fixed-aspect-ratio" />
            {model.getVideoUrl && (
              <div onClick={showOverlayVideo} className="configuration-card__play-icon-wrapper">
                <Icon iconName="action-play" extraClassName="configuration-card__play-icon" size={isWide ? 32 : 24} />
              </div>
            )}
          </div>
        </div>
      )}
      <div className="configuration-card__content-block">
        {model.title ? <RichText field={{ value: model.title }} className="configuration-card__title rich-text"></RichText> : null}
        {model.description ? (
          <RichText
            field={{ value: appendRootToRichTextInternalLinks(model.description) }}
            className="configuration-card__sub-heading rich-text"
          ></RichText>
        ) : null}
      </div>
      <div className="configuration-card__link-wrapper">
        {model.button1?.label && model.button1?.url && (
          <RoutableNotEditableLink
            className="configuration-card__link configuration-card__link--primary"
            href={appendRootToInternalLink(model.button1.url)}
            target={targetLinkdsk({ value: { href: model.button1?.url, target: model.button1?.target } }, isWide)}
            onClick={() => googleClickCtaEvent('Button', model.button1?.label, model.title, model.componentNumber)}
          >
            {model.button1.label}
          </RoutableNotEditableLink>
        )}
        {model.button2?.label && model.button2?.url && (
          <span className={'configuration-card__link configuration-card__link--secondary'}>
            <RoutableNotEditableLink
              href={appendRootToInternalLink(model.button2.url)}
              target={targetLinkdsk({ value: { href: model.button2?.url, target: model.button2?.target } }, isWide)}
              onClick={() => googleClickCtaEvent('Button', model.button2?.label, model.title, model.componentNumber)}
            >
              {model.button2.label}
            </RoutableNotEditableLink>
            <Icon iconName="arrow-chevron-right" size={24} extraClassName="configuration-card__link--icon" />
          </span>
        )}
      </div>
      {model.getVideoUrl && (
        <ModalDialog isOpen={showOverlay} close={closeOverlayModal}>
          <OverlayCarouselPopup slides={getOverlayProps()} isEE={false} />
        </ModalDialog>
      )}
    </article>
  );
};

export default ConfigurationCard;
